import { z } from 'zod';

const zImageResizingUrls = z.object({
  small: z.string().nullable(),
  medium: z.string().nullable(),
  extraLarge: z.string().nullable(),
  original: z.string().nullable(),
});

const zBloomFontFamily = z.object({
  url: z.union([z.string(), z.undefined()]),
  font_family: z.union([z.string(), z.undefined()]),
});

const zBloomStore = z.object({
  address: z.string(),
  city: z.string(),
  id: z.number(),
  name: z.string(),
  recreational: z.boolean(),
  state: z.string(),
  time_zone_identifier: z.string(),
  zip: z.string(),
});

const zStoreSlugEntry = z.object({
  store_id: z.number(),
  kind: z.enum(['primary', 'alias']),
  current_slug: z.string().optional(),
});

const zStoreSlugs = z.record(z.string(), zStoreSlugEntry);

const zColorHash = z.object({
  main: z.union([z.string(), z.undefined()]),
  contrast: z.union([z.string(), z.undefined()]),
});

const zLineageColorHash = z.object({
  cbd: z.union([zColorHash, z.undefined()]),
  hybrid: z.union([zColorHash, z.undefined()]),
  indica: z.union([zColorHash, z.undefined()]),
  sativa: z.union([zColorHash, z.undefined()]),
});

interface Link {
  destination?: string | null;
  label: string;
  links?: Link[];
}

const zRecursiveLink: z.ZodType<Link> = z.lazy(() =>
  z.object({
    label: z.string(),
    destination: z.union([z.string(), z.null()]).optional(),
    links: z.array(zRecursiveLink).optional(),
  })
);

const zSocialLink = z.object({
  label: z.string().nullish(),
  links: z.array(zRecursiveLink).nullish(),
});

const zAgeGateType = z.object({
  cookie_name: z.string().nullable().optional(),
  enabled: z.boolean().nullable().optional(),
  message: z.string().nullable().optional(),
  store_ids: z.array(z.number()).optional(),
  button_order: z.enum(['no_yes', 'yes_no']).nullable().optional(),
});

const zAgeGate = z.object({
  enabled: z.boolean().nullable().optional(),
  default: z.enum(['date_picker', 'yes_no']).nullable().optional(),
  yes_no: zAgeGateType.optional(),
  date_picker: zAgeGateType.optional(),
});

export type AgeGate = z.infer<typeof zAgeGate>;

const zLoginPrompt = z.object({
  header: z.string().nullable().optional(),
  desktop_line_1: z.string().nullable().optional(),
  desktop_line_2: z.string().nullable().optional(),
  desktop_line_3: z.string().nullable().optional(),
  mobile_header: z.string().nullable().optional(),
  mobile_copy: z.string().nullable().optional(),
  cta_label: z.string().nullable().optional(),
  colors: z
    .object({
      button: z.string().nullable().optional(),
      button_text: z.string().nullable().optional(),
    })
    .optional(),
});

const zBloomTheme = z.object({
  colors: z
    .object({
      primary: zColorHash.optional(),
      secondary: zColorHash.optional(),
      header: zColorHash.optional(),
      deals: zColorHash.optional(),
      lineage: zLineageColorHash.optional(),
    })
    .optional(),
  fonts: z
    .object({
      heading: zBloomFontFamily.optional(),
      body: zBloomFontFamily.optional(),
    })
    .optional(),
  product_card_size: z
    .union([z.literal('slim'), z.literal('standard'), z.literal('full')])
    .optional(),
  age_gate: zAgeGate.optional(),
  login_prompt: zLoginPrompt.optional(),
  main_menu: z.array(zRecursiveLink).optional(),
});

/**
 * The first iteration of the Bloom theme in which you could set values that pass
 * straight through to Reefer's theme provider.
 */
const zReeferTheme = z.object({
  fonts: z
    .object({
      default: z.object({
        url: z.union([z.string(), z.undefined()]),
        fontFamily: z.union([z.string(), z.undefined()]),
      }),
    })
    .optional(),
  colors: z
    .object({
      text: z
        .object({
          info: z.string().optional(),
          main: z.string().optional(),
          inverse: z.string().optional(),
        })
        .optional(),
      accent: z.string().optional(),
      background: z.string().optional(),
      supporting: z.string().optional(),
    })
    .optional(),
});

const zLogo = z.object({
  large: z.union([z.string(), z.null(), z.undefined()]),
  medium: z.union([z.string(), z.null(), z.undefined()]),
  small: z.union([z.string(), z.null(), z.undefined()]),
  original: z.union([z.string(), z.null(), z.undefined()]),
});

const zCustomSeo = z.object({
  title: z.string().optional(),
  description: z.string().optional(),
});

const zPartnerScript = z.object({
  script: z.string(),
  storeIds: z.array(z.number()).optional(),
  name: z.string().optional(),
});

const zMobileLayoutItem = z.union([
  z.literal('calls_to_action'),
  z.literal('social_links'),
  z.literal('links'),
  z.literal('image'),
  z.literal('text'),
]);

const zBloomFooterTemplate = z.object({
  calls_to_action: z
    .array(
      z.object({
        header: z.string().nullable().optional(),
        body: z.string().nullable().optional(),
        button_label: z.string().nullable().optional(),
        destination: z.string().nullable().optional(),
      })
    )
    .optional(),
  colors: z
    .object({
      primary: z.string().optional(),
      background: z.string().optional(),
    })
    .optional(),
  enabled: z.boolean().nullable().optional(),
  id: z.number().optional(),
  image: z.union([zImageResizingUrls, z.null()]).optional(),
  layout: z
    .union([z.literal('align_left'), z.literal('align_right')])
    .nullable()
    .optional(),
  links: z.array(zRecursiveLink).optional(),
  mobile_layout: z.array(zMobileLayoutItem).optional(),
  social_links: z.union([zSocialLink, z.null()]).optional(),
  status: z
    .union([z.literal('draft'), z.literal('published')])
    .nullable()
    .optional(),
  text_content: z.string().nullable().optional(),
  use_logo: z.boolean().nullable().optional(),
});

export const zBloomOperatorConfig = z.object({
  operator: z.object({
    id: z.number(),
    name: z.string(),
  }),
  custom_seo: z.object({
    featured: zCustomSeo.optional(),
    category: zCustomSeo.optional(),
    deal: zCustomSeo.optional(),
    shopAll: zCustomSeo.optional(),
    customRow: zCustomSeo.optional(),
    pdp: zCustomSeo.optional(),
  }),
  footer: z.union([zBloomFooterTemplate, z.null(), z.undefined()]),
  theme: zReeferTheme.extend({ bloom: zBloomTheme.optional() }),
  menu_url: z.string(),
  logo: z.union([zLogo, z.null(), z.undefined()]),
  id: z.number(),
  integration_method: z.union([
    z.literal('wordpress'),
    z.literal('cloudflare'),
    z.literal('vercel'),
    z.literal('azure'),
    z.undefined(),
  ]),
  stores: z.array(zBloomStore),
  store_slugs: z.union([zStoreSlugs, z.undefined()]),
  google_sso_client_id: z.union([z.string(), z.null(), z.undefined()]),
  google_sso_client_secret: z.union([z.string(), z.null(), z.undefined()]),
  google_tag_manager_container_id: z.union([
    z.string(),
    z.null(),
    z.undefined(),
  ]),
  menu_token: z.string(),
  partner_scripts: z.union([z.array(zPartnerScript), z.null(), z.undefined()]),
});

const zBloomMenuTemplateType = z.union([
  z.literal('footer'),
  z.literal('page'),
]);

export const zBloomMenuTemplate = z.object({
  id: z.number(),
  source_content_selector: z.string().nullable(),
  source_url: z.string(),
  template_type: zBloomMenuTemplateType,
});

export type BloomOperatorConfig = z.infer<typeof zBloomOperatorConfig>;
export type BloomStore = z.infer<typeof zBloomStore>;
export type BloomStoreSlugs = z.infer<typeof zStoreSlugs>;
export type BloomTheme = z.infer<typeof zBloomTheme>;
export type BloomFooterTemplate = z.infer<typeof zBloomFooterTemplate>;
export type BloomFontFamily = z.infer<typeof zBloomFontFamily>;
export type BloomMenuTemplate = z.infer<typeof zBloomMenuTemplate>;
export type BloomMenuTemplateType = z.infer<typeof zBloomMenuTemplateType>;
export type BloomMobileLayoutItem = z.infer<typeof zMobileLayoutItem>;
export type BloomRecursiveLink = z.infer<typeof zRecursiveLink>;
export type BloomPartnerScript = z.infer<typeof zPartnerScript>;
export type BloomMainMenuForm = {
  menuItems: BloomRecursiveLink[];
};

/** In forms, images only need to be represented by a single url */
export interface BloomFooterTemplateFormState extends Omit<
  BloomFooterTemplate,
  'image'
> {
  image?: string | null;
}
